import { Box, Checkbox, Flex, ResponsiveTable, Text, TextButton } from '@modusign/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { useSelectionValues, useUrlQuery } from '@hooks';

import EmptyListLayout from '@app/components/EmptyListLayout';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import SelectLimit from '@app/components/SelectLimit';
import SwitchablePagination from '@app/components/SwitchablePagination';
import { useMainPageHeaderState } from '@app/hooks/useMainPageHeaderState';

import SortableListHeader from '../../../../components/SortableListHeader';
import useExternalDocumentList from '../../hooks/useExternalDocumentList';
import useOrdering from '../../hooks/useOrdering';
import DocumentBatchActionBar from '../DocumentBatchActionBar';
import ExternalDocumentMainLayout from '../ExternalDocumentMainLayout';
import DocumentListItem, {
  SHOW_CATEGORY_VIEWPORT,
  SHOW_DATE_OF_COMPLETION_VIEWPORT,
  SHOW_STATUS_VIEWPORT,
} from './DocumentListItem';
import SearchFilter, { isInitialSearchParams, SearchParams } from './SearchFilter';

function formatStatus(value: string | null): 'EFFECTIVE' | 'EXPIRED' | 'ABORTED' | undefined {
  switch (value) {
    case 'EFFECTIVE':
      return 'EFFECTIVE';
    case 'EXPIRED':
      return 'EXPIRED';
    case 'ABORTED':
      return 'ABORTED';
    default:
      return undefined;
  }
}

function convertFilterToSearchString({ query, filter }: SearchParams): string {
  const searchParams = new URLSearchParams();
  if (query) {
    searchParams.set('q', query);
  }
  if (filter.label) {
    searchParams.set('labelId', filter.label.id);
    searchParams.set('labelName', filter.label.name);
  }
  if (filter.datesOfCompletion?.from) {
    searchParams.set('datesOfCompletionFrom', filter.datesOfCompletion.from);
  }
  if (filter.datesOfCompletion?.to) {
    searchParams.set('datesOfCompletionTo', filter.datesOfCompletion.to);
  }
  if (filter.documentPeriod?.from) {
    searchParams.set('documentPeriodFrom', filter.documentPeriod.from);
  }
  if (filter.documentPeriod?.to) {
    searchParams.set('documentPeriodTo', filter.documentPeriod.to);
  }
  if (filter.status) {
    searchParams.set('status', filter.status);
  }

  return searchParams.toString();
}

function useFilter(): SearchParams {
  const query = useUrlQuery();

  const label =
    query.get('labelId') && query.get('labelName')
      ? {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: query.get('labelId')!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          name: query.get('labelName')!,
        }
      : undefined;

  const filter = {
    query: query.get('q') || '',
    filter: {
      label,
      status: formatStatus(query.get('status')),
      documentPeriod: {
        from: query.get('documentPeriodFrom') || null,
        to: query.get('documentPeriodTo') || null,
      },
      datesOfCompletion: {
        from: query.get('datesOfCompletionFrom') || null,
        to: query.get('datesOfCompletionTo') || null,
      },
    },
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => filter, [JSON.stringify(filter)]);
}

export default function DocumentListPage() {
  const history = useHistory();
  const params = useFilter();

  const {
    ordering: { order, orderBy },
    setOrdering,
  } = useOrdering();

  const [{ externalDocuments: documentRegisteredFilter }] = useMainPageHeaderState();

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  const {
    data: documentList,
    error: documentError,
    isLoading: isDocumentLoading,
    refetch,
    invalidateDocumentList,
  } = useExternalDocumentList({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    orderBy: `${orderBy}:${order}` as any,
    filter: {
      datesOfCompletion: params.filter.datesOfCompletion,
      documentPeriod: params.filter.documentPeriod,
      status: params.filter.status,
      isRegistered: documentRegisteredFilter ? true : undefined,
      labelId: params.filter.label?.id,
    },
    limit,
    page,
    query: params.query,
  });

  const { clear, count, selected, set } = useSelectionValues<string>();

  useEffect(() => {
    setPage(1);
  }, [params]);

  useEffect(() => {
    // 전체, 내가 등록 필터가 바뀌면 선택 상태를 초기화합니다. [QA-996]
    clear();
  }, [clear, documentRegisteredFilter]);

  return (
    <>
      {isDocumentLoading && <GlobalLoadingSpinner />}
      <ExternalDocumentMainLayout>
        <SearchFilter
          value={params}
          onChange={(filter) => {
            history.replace(`/external-documents/list?${convertFilterToSearchString(filter)}`);
            clear();
          }}
        />
        {!isInitialSearchParams(params) && (
          <Text as="div" fontSize="16px" fontWeight="medium" ml={4} mt={7}>
            {documentList?.count}개의 검색 결과
          </Text>
        )}
        <Box mt={6} p={[4, null, 0]}>
          {selected.length > 0 && (
            <DocumentBatchActionBar
              documentList={documentList?.externalDocuments || []}
              selectedDocumentIds={selected}
              onCheckboxClick={() => {
                if (!documentList) {
                  return;
                }

                // 전체 석택되었을 때는 전체 선택 해제
                if (documentList.externalDocuments.length === selected.length) {
                  clear();
                  // 부분 선택되었을 때는 전체 선택하기
                } else if (selected.length > 0) {
                  set(
                    documentList.externalDocuments.map(({ id }) => id),
                    true
                  );
                }
              }}
              onComplete={(needRefetch) => {
                if (needRefetch) {
                  invalidateDocumentList();
                }
                clear();
              }}
            />
          )}
          <ResponsiveTable.Table sx={{ whiteSpace: 'nowrap', tableLayout: 'fixed' }}>
            <thead>
              <ResponsiveTable.THeadRow
                borderBottomWidth={selected.length > 0 ? 0 : 1}
                display="table-row"
                height="47.5px"
                sx={{ visibility: selected.length > 0 ? 'collapse' : undefined }}
              >
                <ResponsiveTable.TH display={['none', null, 'table-cell']} sx={{ width: '40px' }}>
                  <Box mt="7px">
                    <Checkbox
                      // THeadRow의 visibility collapse가 checkbox에는 좀 늦게 적용되어 잠깐 체크박스가 보이는 현상이 있습니다.
                      // 그래서 checkbox에는 별도록 display 설정을 합니다.
                      checked={
                        (documentList?.externalDocuments.length || 0) > 0 &&
                        count === documentList?.externalDocuments.length
                      }
                      disabled={documentList?.externalDocuments.length === 0}
                      display={selected.length > 0 ? 'none' : undefined}
                      onChange={(e) =>
                        set(
                          documentList?.externalDocuments.map((d) => d.id) || [],
                          e.target.checked
                        )
                      }
                    />
                  </Box>
                </ResponsiveTable.TH>
                <ResponsiveTable.TH
                  display={['none', null, 'table-cell']}
                  sx={{ width: '192px', p: 4 }}
                >
                  <SortableListHeader
                    ascDescription="1-10"
                    descDescription="10-1"
                    name="등록번호"
                    order={orderBy === 'registrationNumber' ? order : null}
                    onOrderClick={(order) => setOrdering({ order, orderBy: 'registrationNumber' })}
                  />
                </ResponsiveTable.TH>
                <ResponsiveTable.TH
                  sx={{
                    pl: [4, null, 7],
                    py: 4,
                  }}
                >
                  <SortableListHeader
                    ascDescription="ㄱ-ㅎ"
                    descDescription="ㅎ-ㄱ"
                    name="계약 제목"
                    order={orderBy === 'title' ? order : null}
                    onOrderClick={(order) => setOrdering({ order, orderBy: 'title' })}
                  />
                </ResponsiveTable.TH>
                <ResponsiveTable.TH
                  display="none"
                  sx={{
                    width: '180px',
                    [`@media screen and (min-width: ${SHOW_CATEGORY_VIEWPORT}px)`]: {
                      display: 'table-cell',
                    },
                    p: 4,
                  }}
                >
                  <Text color="textNeutralVeryLight">라벨</Text>
                </ResponsiveTable.TH>
                <ResponsiveTable.TH
                  display="none"
                  sx={{
                    width: '120px',
                    [`@media screen and (min-width: ${SHOW_DATE_OF_COMPLETION_VIEWPORT}px)`]: {
                      display: 'table-cell',
                    },
                    p: 4,
                  }}
                >
                  <SortableListHeader
                    ascDescription="오래된순"
                    descDescription="최신순"
                    name="계약 체결일"
                    order={orderBy === 'dateOfCompletion' ? order : null}
                    onOrderClick={(order) => setOrdering({ order, orderBy: 'dateOfCompletion' })}
                  />
                </ResponsiveTable.TH>
                <ResponsiveTable.TH
                  display="none"
                  sx={{
                    width: '160px',
                    [`@media screen and (min-width: ${SHOW_STATUS_VIEWPORT}px)`]: {
                      display: 'table-cell',
                    },
                    p: 4,
                  }}
                >
                  <SortableListHeader
                    name="계약 상태"
                    order={orderBy === 'status' ? order : null}
                    onOrderClick={(order) => setOrdering({ order, orderBy: 'status' })}
                  />
                </ResponsiveTable.TH>
                <ResponsiveTable.TH
                  display={['none', null, 'table-cell']}
                  sx={{
                    width: '136px',
                    p: 4,
                  }}
                >
                  <SortableListHeader
                    ascDescription="오래된순"
                    descDescription="최신순"
                    name="마지막 활동 시각"
                    order={orderBy === 'updatedAt' ? order : null}
                    onOrderClick={(order) => setOrdering({ order, orderBy: 'updatedAt' })}
                  />
                </ResponsiveTable.TH>
                <ResponsiveTable.TH
                  sx={{
                    width: '74px',
                    textAlign: 'center',
                    p: 4,
                  }}
                >
                  메뉴
                </ResponsiveTable.TH>
              </ResponsiveTable.THeadRow>
            </thead>
            <ResponsiveTable.TBody sx={{ display: 'table-row-group' }}>
              {documentError ? (
                <tr>
                  <td colSpan={8}>
                    <Flex
                      alignItems="center"
                      flexDirection="column"
                      height="200px"
                      justifyContent="center"
                    >
                      <Text>계약 리스트를 불러오지 못했습니다.</Text>
                      <TextButton mt={2} onClick={() => refetch()}>
                        다시 시도하기
                      </TextButton>
                    </Flex>
                  </td>
                </tr>
              ) : (
                documentList &&
                (documentList.externalDocuments.length > 0 ? (
                  documentList.externalDocuments.map((document) => (
                    <DocumentListItem
                      document={document}
                      isSelected={selected.includes(document.id)}
                      key={document.id}
                      onMutationSuccess={() => clear()}
                      onSelect={(e) => set(document.id, e.target.checked)}
                    />
                  ))
                ) : (
                  <EmptyDocumentRow />
                ))
              )}
            </ResponsiveTable.TBody>
          </ResponsiveTable.Table>
          {documentList && documentList.externalDocuments.length > 0 && (
            <Box mt={10} position="relative">
              <SwitchablePagination
                currentPage={page}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                maxPage={Math.ceil(documentList.count! / limit)}
                onClick={setPage}
              />
              <Box
                position="absolute"
                right={0}
                sx={{
                  transform: 'translateY(-50%)',
                }}
                top={[0, null, '50%']}
                width="120px"
              >
                <SelectLimit
                  limit={limit}
                  onChange={(v) => {
                    setLimit(v);
                    setPage(1);
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </ExternalDocumentMainLayout>
    </>
  );
}

function EmptyDocumentRow() {
  return (
    <tr>
      <td colSpan={7}>
        <EmptyListLayout>
          <Text as="p" color="textNeutralLight">
            등록된 계약이 없습니다.
          </Text>
        </EmptyListLayout>
      </td>
    </tr>
  );
}
